import { ACTION_TO_ADD_COMMENT } from 'mk/autogenerated/translations/Comments.sagas.64068f48bbe2b0de0ea9ab84ed28be65'
import {
    blogs_api_create_comment_url,
    blogs_api_delete_comment_url,
    blogs_api_get_comments_url,
} from 'mk/urls/functions';
import {
    COMMENT_CREATE,
    COMMENT_CREATE_UNAUTHENTICATED,
    COMMENT_DELETE,
    COMMENT_REPLY,
    CommentCreateAction,
    commentCreateActions,
    CommentDeleteAction,
    commentDeleteActions,
    CommentReplyAction,
    COMMENTS_LOAD,
    commentsFetch,
    CommentsLoadAction,
} from 'mk2/apps/blogs/containers/Comments/Comments.actions';
import { COMMENT_TEXTAREA } from 'mk2/apps/blogs/helpers';
import { CommentEntity, CommentSchema } from 'mk2/apps/blogs/schemas';
import { getModelAndId } from 'mk2/apps/blogs/utils';
import { ComplaintEntity, ComplaintSchema } from 'mk2/containers/Complaint/Complaint.schemas';
import { toolbarCloseAll } from 'mk2/containers/Toolbar/Toolbar.actions';
import { handleXHRPostErrorSaga, normalizeError, XHRAction, XHRFormError, XHRPostError } from 'mk2/helpers/api';
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from 'mk2/helpers/form';
import { PhotoUploadStatus } from 'mk2/helpers/form.reducers';
import { getLogger } from 'mk2/logger';
import { checkIfUserIsLoggedIn } from 'mk2/sagas/auth';
import { getRoutingLocation } from 'mk2/selectors';
import { JAM_NOTIFICATION, JamNotificationAction } from 'mk2/services/jam.actions';
import { trackReach, TrackReachProduct } from 'mk2/trackReach';
import { normalize } from 'normalizr';
import { change, formValueSelector, startSubmit } from 'redux-form';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

const logger = getLogger('blogs/Comments.sagas');

interface ApiResponseComments {
    body: {
        canAdd: boolean;
        comments: CommentEntity[];
        complaints: ComplaintEntity;
    };
}

function* fetchComments({ key, xhr }: CommentsLoadAction & XHRAction) {
    yield fetchShared(key, xhr);
}

function* fetchComments2({ channel, xhr }: JamNotificationAction & XHRAction) {
    const splitted = channel.split(':');
    const key = `${splitted[1]}/${splitted[2]}`;
    yield fetchShared(key, xhr);
}

function* fetchShared(key, xhr) {
    const modelAndId = getModelAndId(key);

    yield put(commentsFetch.request(key));

    try {
        const response: ApiResponseComments = yield call(() => xhr.get(blogs_api_get_comments_url(modelAndId[0], modelAndId[1])));
        const normalizedResponse = normalize({
            comments: response.body.comments,
            complaints: response.body.complaints,
        }, {
            comments: [CommentSchema],
            complaints: [ComplaintSchema],
        });

        yield put(commentsFetch.success(key, response.body.canAdd, {
            entities: normalizedResponse.entities,
            result: normalizedResponse.result.comments,
        }));
    } catch (error) {
        logger.error(error);
        yield put(commentsFetch.failure(key, normalizeError(error)));
    }
}

function* deleteComment({ commentId, key, xhr }: CommentDeleteAction & XHRAction) {
    const model = getModelAndId(key)[0];

    yield put(commentDeleteActions.request(commentId, key));

    try {
        const response = yield call(() => xhr.post(blogs_api_delete_comment_url(model, commentId), {}));
        const normalizedResponse = normalize(response.body.comment, CommentSchema);

        yield put(commentDeleteActions.success(commentId, key, normalizedResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(commentDeleteActions.failure(commentId, key, normalizeError(error)));
    }
}

interface ApiResponseCreateComment {
    body: {
        comment: CommentEntity,
        targetPostCommercialDetails: {
            /**
             * keep format in sync with export.py
             */
            relatedToDaysWith: string[],
            relatedToAmbassadorOfBrands: number[],
            relatedToTesting: string[],
        };
    };
}

function* createComment({ commentFd, key, xhr }: CommentCreateAction & XHRAction) {
    const modelAndId = getModelAndId(key);
    const model = modelAndId[0];
    const id = parseFloat(modelAndId[1]);
    const location = yield select(getRoutingLocation);

    // Start submit
    yield put(startSubmit(COMMENT_TEXTAREA(key)));

    // close toolbar, e.g. photos might be opened
    yield put(toolbarCloseAll(COMMENT_TEXTAREA(key)));

    yield put(commentCreateActions.request(commentFd, key));
    try {
        const response: ApiResponseCreateComment = yield call(() => xhr.post(blogs_api_create_comment_url(model, id), {
            ...commentFd,
            text: commentFd.text,
            photos: commentFd.photos ? commentFd.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS) : [],
            sticker: commentFd.sticker || null,
            asAdmin: commentFd.asAdmin || false,
        }));
        const normalizedResponse = normalize(response.body.comment, CommentSchema);

        const details = response.body.targetPostCommercialDetails;
        if (details.relatedToDaysWith && details.relatedToDaysWith.length > 0) {
            trackReach(
                location,
                TrackReachProduct.DAYS_WITH,
                details.relatedToDaysWith[0],
                'blog',
            );
        }
        if (details.relatedToAmbassadorOfBrands && details.relatedToAmbassadorOfBrands.length > 0) {
            trackReach(
                location,
                TrackReachProduct.AMBASSADOR,
                details.relatedToAmbassadorOfBrands[0],
                'blog',
            );
        }
        if (details.relatedToTesting && details.relatedToTesting.length > 0) {
            trackReach(
                location,
                TrackReachProduct.TESTING,
                details.relatedToTesting[0],
                'blog',
            );
        }

        yield handleXHRFormSuccessSaga(COMMENT_TEXTAREA(key));
        yield put(commentCreateActions.success(commentFd, key, normalizedResponse));
    } catch (error) {
        yield handleXHRFormErrorSaga(COMMENT_TEXTAREA(key), error as XHRFormError, logger);
        yield put(commentCreateActions.failure(commentFd, key, normalizeError(error)));
    }
}

function* commentReply({ key, username }: CommentReplyAction) {
    const form = COMMENT_TEXTAREA(key);
    const text = yield select(formValueSelector(form), 'text');
    const mention = username ? '@' + username + ' ' : '';
    const value = text + (text && mention ? '\n' : '') + mention;
    // Change form
    yield put(change(form, 'text', value));
}

function* createCommentUnauthenticated() { /* this saga runs only in case the user is not logged in */
    if (!(yield call(checkIfUserIsLoggedIn, ACTION_TO_ADD_COMMENT))) {
        return;
    }
}

export default function* root() {
    yield all([
        takeLatest(COMMENTS_LOAD, fetchComments),
        takeEvery(COMMENT_DELETE, deleteComment),
        takeEvery(COMMENT_CREATE, createComment),
        takeEvery(COMMENT_CREATE_UNAUTHENTICATED, createCommentUnauthenticated),
        takeLatest(COMMENT_REPLY, commentReply),
        takeLatest(
            (action: JamNotificationAction) => action.type === JAM_NOTIFICATION && action.channel.startsWith('comment:'),
            fetchComments2,
        ),
    ]);
}
